/**
 * Client-side validation for the profile demographics form (FAM-114).
 *
 * These are lenient pre-checks that mirror the server's acceptance range so the user gets
 * inline feedback without a round trip; the server stays authoritative. Keep the phone
 * bounds in sync with `PhoneNumberE164` (app/utils/phone.py) and the name bound in sync
 * with `ProfileName` (app/profile/schemas.py).
 *
 * Rules return react-intl descriptors rather than formatted strings so this module stays
 * free of react-intl and can be unit tested without a provider.
 */

export type MessageCopy = { id: string; defaultMessage: string }

export const MAX_NAME_LENGTH = 100
export const MAX_PHONE_INPUT_LENGTH = 20
export const MIN_PHONE_DIGITS = 10
export const MAX_PHONE_DIGITS = 15

const PHONE_ALLOWED_CHARACTERS = /^\+?[\d\s().-]+$/

export type PhoneValidationErrorKind = 'invalidCharacters' | 'tooLong' | 'invalidFormat'

/** Returns the kind of phone failure, or null when the value is acceptable. */
export function getPhoneValidationErrorKind(value: string): PhoneValidationErrorKind | null {
  if (!PHONE_ALLOWED_CHARACTERS.test(value)) return 'invalidCharacters'
  if (value.length > MAX_PHONE_INPUT_LENGTH) return 'tooLong'
  const digits = value.replace(/\D/g, '')
  if (digits.length > MAX_PHONE_DIGITS) return 'tooLong'
  if (digits.length < MIN_PHONE_DIGITS) return 'invalidFormat'
  return null
}

export function isValidEmailFormat(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

// FAM-114 specifies copy for unsupported characters and for too many digits. `invalidFormat`
// (valid characters but an implausible digit count) has no AC copy, so it keeps the generic
// message. A Record keeps this exhaustive: adding a kind becomes a compile error.
const PHONE_ERROR_COPY: Record<PhoneValidationErrorKind, MessageCopy> = {
  invalidCharacters: {
    id: 'profile.editModal.phoneInvalidCharacters',
    defaultMessage:
      'Phone numbers can only include numbers and the symbols + ( ) - and spaces. Please remove any other characters.',
  },
  tooLong: {
    id: 'profile.editModal.phoneTooLong',
    defaultMessage: "That's more digits than a phone number can have. Please check the number and try again.",
  },
  invalidFormat: {
    id: 'profile.editModal.phoneInvalid',
    defaultMessage: 'Enter a valid phone number.',
  },
}

const NAME_TOO_LONG_COPY: MessageCopy = {
  id: 'profile.editModal.nameTooLong',
  defaultMessage: 'Please shorten your name to 100 characters or fewer.',
}

// A user who never had a name (nothing in our records, nothing from Auth0) isn't forced to add
// one just to edit something else; this only fires when an existing name is blanked out.
const NAME_REQUIRED_COPY: Record<'firstName' | 'lastName', MessageCopy> = {
  firstName: { id: 'profile.editModal.firstNameRequired', defaultMessage: 'First name is required.' },
  lastName: { id: 'profile.editModal.lastNameRequired', defaultMessage: 'Last name is required.' },
}

const EMAIL_INVALID_COPY: MessageCopy = {
  id: 'profile.editModal.emailInvalid',
  defaultMessage: 'Enter a valid email address.',
}

export type DemographicsField = 'firstName' | 'lastName' | 'emailAddress' | 'phoneNumber'

export type DemographicsFormValues = Partial<Record<DemographicsField, string | null | undefined>>

export type DemographicsFieldErrors = Partial<Record<DemographicsField, MessageCopy>>

/** Field order drives which input receives focus when a submission fails. */
export const DEMOGRAPHICS_FIELD_ORDER: DemographicsField[] = ['firstName', 'lastName', 'emailAddress', 'phoneNumber']

export type ValidateFieldOptions = {
  /**
   * For firstName/lastName only: the value the field held before this edit session. Lets
   * "blanked out an existing name" (rejected) be told apart from "never had one, untouched"
   * (allowed) — see NAME_REQUIRED_COPY.
   */
  originalValue?: string | null
}

/**
 * Validates one field. Email and phone are trimmed because surrounding whitespace is a typo;
 * names are compared untrimmed to match the server's `max_length` check on the raw string.
 */
export function validateField(
  field: DemographicsField,
  value: string | null | undefined,
  options: ValidateFieldOptions = {}
): MessageCopy | null {
  const raw = value ?? ''
  if (field === 'firstName' || field === 'lastName') {
    if (!raw.trim() && (options.originalValue ?? '').trim()) return NAME_REQUIRED_COPY[field]
    return raw.length > MAX_NAME_LENGTH ? NAME_TOO_LONG_COPY : null
  }
  const trimmed = raw.trim()
  // Both contact fields are optional on their own; the "can't clear both" guardrail is a
  // form-level concern handled by the caller.
  if (!trimmed) return null
  if (field === 'emailAddress') {
    return isValidEmailFormat(trimmed) ? null : EMAIL_INVALID_COPY
  }
  const kind = getPhoneValidationErrorKind(trimmed)
  return kind ? PHONE_ERROR_COPY[kind] : null
}

export type DemographicsOriginalValues = Partial<Record<'firstName' | 'lastName', string | null | undefined>>

/** Validates every field so the user sees all problems at once rather than one per submit. */
export function validateDemographics(
  values: DemographicsFormValues,
  originalValues: DemographicsOriginalValues = {}
): DemographicsFieldErrors {
  const errors: DemographicsFieldErrors = {}
  DEMOGRAPHICS_FIELD_ORDER.forEach((field) => {
    const originalValue = field === 'firstName' || field === 'lastName' ? originalValues[field] : undefined
    const error = validateField(field, values[field], { originalValue })
    if (error) errors[field] = error
  })
  return errors
}

// What to show when the server rejects a field this client accepted. Email can only fail the
// one rule we already mirror, and the server can reject a phone number for reasons we
// deliberately don't check (a leading zero after the country code, say), so the generic message
// is the only honest one there.
const SERVER_ERROR_COPY: Record<'emailAddress' | 'phoneNumber', MessageCopy> = {
  emailAddress: EMAIL_INVALID_COPY,
  phoneNumber: PHONE_ERROR_COPY.invalidFormat,
}

// A name field has two distinct server-side failure reasons now, so `loc` alone isn't enough —
// `type` picks between them. `type` is a small fixed identifier pydantic/our own validator
// assigns (e.g. "string_too_long"), never free text a user typed, so reading it doesn't risk
// rendering server-authored text.
function nameServerErrorCopy(field: 'firstName' | 'lastName', type: unknown): MessageCopy {
  return type === 'string_too_long' ? NAME_TOO_LONG_COPY : NAME_REQUIRED_COPY[field]
}

/**
 * Maps a FastAPI validation body — `{ detail: [{ loc: ['body', 'phoneNumber'], ... }] }` — onto
 * our own copy, keyed by field. Only `loc`/`type` are read: the server's `msg` is written for
 * API consumers and must never reach the UI. Anything unrecognized yields no errors, letting the
 * caller fall back to its generic failure message.
 */
export function mapServerValidationErrors(detail: unknown): DemographicsFieldErrors {
  if (!Array.isArray(detail)) return {}
  const errors: DemographicsFieldErrors = {}
  detail.forEach((entry) => {
    const loc = (entry as { loc?: unknown } | null)?.loc
    const type = (entry as { type?: unknown } | null)?.type
    if (!Array.isArray(loc)) return
    loc.forEach((segment) => {
      const field = DEMOGRAPHICS_FIELD_ORDER.find((candidate) => candidate === segment)
      if (!field || errors[field]) return
      errors[field] =
        field === 'firstName' || field === 'lastName' ? nameServerErrorCopy(field, type) : SERVER_ERROR_COPY[field]
    })
  })
  return errors
}
